import { useCallback, useEffect, useState } from "react";
import { Alert, AlertTitle, CircularProgress, Box, Typography } from "@mui/material";

import { callApi } from "@/dataprovider/misc-apis";
import {
  deriveLeaveBalancePlainSummary,
  isLeaveBalanceEndpointMissing,
  leaveBalanceApiLocation,
  parseLeaveBalancePayload,
  type LeaveBalanceEnabled,
} from "@/containers/staff/shared/leave-balance";

type Props = {
  employeeId?: number | null;
  mode?: "office" | "self";
  /** When the leave request payload already includes balance fields (optimistic seed only). */
  embeddedBalance?: unknown;
  /**
   * Change this after approve/decline/cancel (e.g. leave status) so the alert
   * reloads from the live balance endpoint instead of staying on a stale fetch.
   */
  refreshKey?: string | number | null;
};

type AlertState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "hidden" }
  | { status: "ready"; balance: LeaveBalanceEnabled };

/**
 * Leave balance on create/show — same plain-language summary as the staff card
 * (yearly remaining + pending/allowance hints).
 */
export function LeaveBalanceAlert({
  employeeId,
  mode = "office",
  embeddedBalance,
  refreshKey,
}: Props) {
  const [state, setState] = useState<AlertState>({ status: "idle" });

  const applyParsed = useCallback((data: unknown): boolean => {
    const parsed = parseLeaveBalancePayload(data);
    if (!parsed || !parsed.enabled) {
      setState({ status: "hidden" });
      return Boolean(parsed && !parsed.enabled);
    }
    setState({ status: "ready", balance: parsed });
    return true;
  }, []);

  useEffect(() => {
    if (mode === "office" && (employeeId == null || employeeId < 1)) {
      setState({ status: "idle" });
      return;
    }

    let cancelled = false;
    if (embeddedBalance != null) {
      applyParsed(embeddedBalance);
    } else {
      setState({ status: "loading" });
    }

    callApi({
      location: leaveBalanceApiLocation(mode, employeeId ?? undefined),
      method: "GET",
    })
      .then((response) => {
        if (cancelled) return;
        applyParsed(response.data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (isLeaveBalanceEndpointMissing(err)) {
          // Keep optimistic embedded parse if we already showed one.
          setState((prev) => (prev.status === "ready" ? prev : { status: "hidden" }));
          return;
        }
        setState((prev) => (prev.status === "ready" ? prev : { status: "hidden" }));
      });

    return () => {
      cancelled = true;
    };
  }, [applyParsed, embeddedBalance, employeeId, mode, refreshKey]);

  if (state.status === "idle" || state.status === "hidden") {
    return null;
  }

  if (state.status === "loading") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  const summary = deriveLeaveBalancePlainSummary(state.balance);

  return (
    <Alert severity={summary.alertSeverity} sx={{ mb: 1.5, width: "100%" }}>
      <AlertTitle sx={{ mb: summary.supportingLines.length ? 0.5 : 0 }}>
        {summary.headline}
      </AlertTitle>
      {summary.supportingLines.map((line) => (
        <Typography key={line} variant="body2" component="div">
          {line}
        </Typography>
      ))}
      {state.balance.financialYear ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          FY {state.balance.financialYear}
        </Typography>
      ) : null}
    </Alert>
  );
}
